import { useState, useEffect } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Snackbar,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import InventoryIcon from '@mui/icons-material/Inventory'
import { adminOrderService } from '../services'
import useAuthStore from '../store/authStore'
import { hasAdminPermission, PERMISSION_SUBJECTS } from '../utils/adminPermissions'

const LowStockSettingsPage = () => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canWrite = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.INVENTORY_THRESHOLDS,
    'write',
  )
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [threshold, setThreshold] = useState('')
  const [fieldError, setFieldError] = useState(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await adminOrderService.getInventoryGlobalSettings()
      const value = data?.low_stock_threshold
      setThreshold(value !== undefined && value !== null ? String(value) : '10')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load inventory settings')
    } finally {
      setLoading(false)
    }
  }

  const validateThreshold = (raw) => {
    const trimmed = String(raw).trim()
    if (trimmed === '') {
      return 'Low stock threshold is required'
    }
    const num = Number(trimmed)
    if (!Number.isInteger(num) || num < 1) {
      return 'Enter a whole number of 1 or greater'
    }
    return null
  }

  const handleSave = async () => {
    if (!canWrite) return
    const validationError = validateThreshold(threshold)
    if (validationError) {
      setFieldError(validationError)
      return
    }
    setFieldError(null)

    try {
      setSaving(true)
      setError(null)
      const num = Number(threshold)
      const data = await adminOrderService.updateInventoryGlobalSettings(num)
      setThreshold(String(data.low_stock_threshold))
      setSuccessMessage('Inventory settings saved')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save inventory settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <InventoryIcon sx={{ color: '#e90000', fontSize: 32 }} />
        <Typography variant="h5" fontWeight={600}>
          Inventory thresholds
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Low stock threshold
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Admin inventory rows with quantity on hand from 1 through this value count as low
            stock on the dashboard. Rows with quantity 0 are reported separately as out of stock.
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault()
                handleSave()
              }}
              sx={{ maxWidth: 360 }}
            >
              <TextField
                fullWidth
                label="Low stock threshold (units)"
                type="number"
                value={threshold}
                onChange={(e) => {
                  setThreshold(e.target.value)
                  if (fieldError) setFieldError(null)
                }}
                error={!!fieldError}
                helperText={fieldError || 'Integer of 1 or greater (default 10)'}
                inputProps={{ min: 1, step: 1 }}
                disabled={saving || !canWrite}
              />
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  disabled={saving || !canWrite}
                  sx={{
                    bgcolor: '#e90000',
                    '&:hover': { bgcolor: '#c40000' },
                  }}
                >
                  Save
                </Button>
                <Button variant="outlined" onClick={loadSettings} disabled={loading || saving}>
                  Reset
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Container>
  )
}

export default LowStockSettingsPage
